import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authServices } from '@/shared/services/auth.services';
import * as wishlistApi from '@/shared/services/wishlist.service';
import { trackMetaAddToWishlist } from '@/shared/analytics/metaPixel';

/** Coalesces overlapping `fetchWishlist` calls (e.g. Header + /wishlist page, or React Strict Mode). */
let fetchWishlistInFlight: Promise<void> | null = null;

export interface WishlistItem {
    id: string;
    slug: string;
    title: string;
    price: number;
    compare_at_price: number;
    primary_image: string;
    brand_name?: string;
    rating?: number;
    review_count?: number;
    stock?: number;
    out_of_stock?: boolean;
    /** Rupees earned on this product, already computed server-side. `null`/absent = no chip. */
    cashback?: number | null;
    /** When true, wishlist cards mask the selling price and hide the discount percentage. */
    hide_coupon_section?: boolean;
}

interface WishlistState {
    items: WishlistItem[];
    isLoading: boolean;
    error: string | null;

    addItem: (item: WishlistItem) => Promise<void>;
    removeItem: (id: string) => Promise<void>;
    clearWishlist: () => void;
    isInWishlist: (id: string) => boolean;
    toggleItem: (item: WishlistItem) => Promise<void>;
    getTotalItems: () => number;
    fetchWishlist: () => Promise<void>;
    syncWithApi: () => Promise<void>;
}

export const useWishlistStore = create<WishlistState>()(
    persist(
        (set, get) => ({
            items: [],
            isLoading: false,
            error: null,

            addItem: async (item) => {
                const items = get().items;
                const exists = items.some((i) => i.id === item.id);
                if (exists) return;

                // Fire once per successful local add (guest or optimistic authed).
                // API 409 is treated as already-wishlisted, so we still keep the
                // optimistic row and do not re-fire if the user somehow double-taps.
                const reportAddToWishlist = () => {
                    trackMetaAddToWishlist({
                        productId: item.id,
                        price: item.price,
                        quantity: 1,
                        title: item.title,
                    });
                };

                if (authServices.isAuthenticated()) {
                    set((state) => ({ items: [...state.items, item], error: null }));
                    reportAddToWishlist();
                    try {
                        await wishlistApi.addToWishlist(item.id);
                    } catch (error) {
                        const status = (error as { response?: { status?: number } })?.response?.status;
                        if (status === 409) {
                            // Product already in wishlist (idempotent) – keep optimistic update
                            return;
                        }
                        console.error('Failed to add item to wishlist API:', error);
                        set((state) => ({
                            items: state.items.filter((i) => i.id !== item.id),
                            error: 'Failed to add to wishlist',
                        }));
                    }
                } else {
                    set({ items: [...items, item] });
                    reportAddToWishlist();
                }
            },

            removeItem: async (id) => {
                set((state) => ({ items: state.items.filter((i) => i.id !== id), error: null }));

                if (authServices.isAuthenticated()) {
                    try {
                        await wishlistApi.removeFromWishlist(id);
                    } catch (error) {
                        console.error('Failed to remove item from wishlist API:', error);
                        set({ error: 'Failed to remove from wishlist' });
                    }
                }
            },

            clearWishlist: () => set({ items: [], error: null }),

            isInWishlist: (id) => get().items.some((i) => i.id === id),

            toggleItem: async (item) => {
                const items = get().items;
                const exists = items.some((i) => i.id === item.id);

                if (exists) {
                    await get().removeItem(item.id);
                } else {
                    await get().addItem(item);
                }
            },

            getTotalItems: () => get().items.length,

            fetchWishlist: async () => {
                if (!authServices.isAuthenticated()) return;
                if (fetchWishlistInFlight) return fetchWishlistInFlight;

                fetchWishlistInFlight = (async () => {
                    set({ isLoading: true, error: null });
                    try {
                        const data = await wishlistApi.fetchWishlist();
                        const rawItems = data?.items ?? [];
                        const previousHideCoupon = new Map(
                            get().items.map((existing) => [existing.id, existing.hide_coupon_section]),
                        );
                        const items = Array.isArray(rawItems)
                            ? rawItems.map((raw) => {
                                const mapped = wishlistApi.mapWishlistItemApiToUi(raw);
                                if (mapped.hide_coupon_section == null && previousHideCoupon.get(mapped.id) === true) {
                                    return { ...mapped, hide_coupon_section: true };
                                }
                                return mapped;
                            })
                            : [];
                        set({ items, isLoading: false });
                    } catch (error) {
                        console.error('Failed to fetch wishlist:', error);
                        set({ isLoading: false, error: 'Failed to fetch wishlist' });
                    } finally {
                        fetchWishlistInFlight = null;
                    }
                })();

                return fetchWishlistInFlight;
            },

            syncWithApi: async () => {
                if (!authServices.isAuthenticated()) return;

                set({ isLoading: true, error: null });
                const items = get().items;
                const productIds = items.map((i) => i.id);

                try {
                    if (productIds.length > 0) {
                        await wishlistApi.mergeWishlist(productIds);
                    }
                    await get().fetchWishlist();
                } catch (error) {
                    console.error('Failed to merge wishlist:', error);
                    set({ isLoading: false, error: 'Failed to sync wishlist' });
                }
            },
        }),
        {
            name: 'wishlist-storage',
            onRehydrateStorage: () => (state) => {
                if (state && !Array.isArray(state.items)) {
                    console.warn('Detected corrupted wishlist items state, resetting to empty array');
                    state.items = [];
                }
            },
        }
    )
);

if (typeof window !== 'undefined') {
    window.addEventListener('userLogout', () => {
        useWishlistStore.getState().clearWishlist();
    });
}
